import { useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import { getModules } from '@/services/moduleService';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { ModuleDto } from '@/types/module';
import type { FilterFieldConfig } from '@/types/filters';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/modules/')({
  validateSearch: validateTableSearch,
  component: ModulesPage,
});

// ============================================
// HELPERS
// ============================================

function StatusBadge({ status }: { status: ModuleDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{toTitleCase(status)}</span>;
}

function DefaultBadge({ isDefault }: { isDefault: boolean }) {
  const { t } = useTranslation();
  return (
    <span className={`${styles.badge} ${isDefault ? styles.badgeActive : styles.badgeDefault}`}>
      {isDefault ? t('K_YES', 'Yes') : t('K_NO', 'No')}
    </span>
  );
}

// ============================================
// COMPONENT
// ============================================

function ModulesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Filter fields ordered to match table columns:
  // Name, Identifier, Description, Default, Status
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'name',
        label: t('K_COLUMN_NAME', 'Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_NAME', 'Search name...'),
      },
      {
        fieldKey: 'identifier',
        label: t('K_COLUMN_IDENTIFIER', 'Identifier'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_IDENTIFIER', 'Search identifier...'),
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/modules',
    filtersParam,
    fields: filterFields,
  });

  const columns = useMemo<DataTableColumn<ModuleDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>{item.name || '-'}</span>
        ),
      },
      {
        id: 'identifier',
        header: t('K_COLUMN_IDENTIFIER', 'Identifier'),
        sortable: true,
        sortKey: 'identifier',
        cell: (item) => item.identifier || '-',
      },
      {
        id: 'description',
        header: t('K_COLUMN_DESCRIPTION', 'Description'),
        cell: (item) => <span title={item.description || '-'}>{item.description || '-'}</span>,
      },
      {
        id: 'isDefault',
        header: t('K_COLUMN_DEFAULT', 'Default'),
        cell: (item) => <DefaultBadge isDefault={item.isDefault} />,
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
    ],
    [t],
  );

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/modules',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/modules',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/modules',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['modules', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getModules({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: ModuleDto) => item.id, []);

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_MODULES', 'No modules found')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_MODULES_DESC', 'Modules will appear here once they are configured.')}
          </p>
        </div>
      ),
    [listForbidden, t],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_MODULE_REGISTRY', 'Module Registry')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_MODULES', 'View available system modules')}</p>
        </div>
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        loading={isLoading}
        fetching={isFetching}
        loadingText={t('K_LOADING_MODULES', 'Loading modules...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        aria-label="Modules list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />
    </div>
  );
}
